import {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useSelector } from 'react-redux'
import { axiosInstance } from '../helpers'
import { toast } from 'react-toastify'
import { RootState } from '../store'

export interface IVendorInvoiceContext {
    invoiceCode: string
    setInvoiceCode: Dispatch<SetStateAction<string>>
    step: number
    setStep: Dispatch<SetStateAction<number>>
}

export const VendorInvoiceContext = createContext<IVendorInvoiceContext>(
    (null as any) as IVendorInvoiceContext,
)

export const INVOICE_CODE_STEP = 1
export const SEND_INVOICE_STEP = 2

export const VENDOR_INVOICE_STEP_KEY = 'vendor_invoice_step'

interface StoredVendorInvoiceStep {
    vendorId: number
    step: number
}

const isStoredVendorInvoiceStep = (
    value: unknown,
): value is StoredVendorInvoiceStep => {
    if (value == null || typeof value !== 'object') {
        return false
    }
    const stored = value as StoredVendorInvoiceStep
    return (
        typeof stored.vendorId === 'number' && typeof stored.step === 'number'
    )
}

const readStoredVendorInvoice = (): StoredVendorInvoiceStep | null => {
    if (typeof window === 'undefined') {
        return null
    }
    const raw = sessionStorage.getItem(VENDOR_INVOICE_STEP_KEY)
    if (!raw) {
        return null
    }
    // Legacy value was a bare step number, which unlocked every vendor in the session.
    if (
        raw === String(SEND_INVOICE_STEP) ||
        raw === String(INVOICE_CODE_STEP)
    ) {
        sessionStorage.removeItem(VENDOR_INVOICE_STEP_KEY)
        return null
    }
    try {
        const parsed: unknown = JSON.parse(raw)
        return isStoredVendorInvoiceStep(parsed) ? parsed : null
    } catch {
        sessionStorage.removeItem(VENDOR_INVOICE_STEP_KEY)
        return null
    }
}

const writeStoredVendorInvoice = (value: StoredVendorInvoiceStep | null) => {
    if (typeof window === 'undefined') {
        return
    }
    if (value == null) {
        sessionStorage.removeItem(VENDOR_INVOICE_STEP_KEY)
        return
    }
    sessionStorage.setItem(VENDOR_INVOICE_STEP_KEY, JSON.stringify(value))
}

export const clearVendorInvoiceStep = () => {
    writeStoredVendorInvoice(null)
}

export const hasValidatedVendorInvoiceCode = (
    vendorId?: number | null,
): boolean => {
    if (vendorId == null) {
        return false
    }
    const stored = readStoredVendorInvoice()
    return stored?.vendorId === vendorId && stored.step === SEND_INVOICE_STEP
}

export const useVendorInvoiceState = (): IVendorInvoiceContext => {
    const vendorId = useSelector(
        (state: RootState) => state.user.workspaceUser?.active_workspace?.id,
    )
    const vendorIdRef = useRef(vendorId)
    const previousVendorIdRef = useRef(vendorId)
    vendorIdRef.current = vendorId

    const [invoiceCode, setInvoiceCode] = useState<string>('')
    const [unlockedVendorId, setUnlockedVendorId] = useState<number | null>(
        () => {
            const stored = readStoredVendorInvoice()
            return stored?.step === SEND_INVOICE_STEP ? stored.vendorId : null
        },
    )

    useEffect(() => {
        const previousVendorId = previousVendorIdRef.current
        previousVendorIdRef.current = vendorId

        // Ignore workspace hydration (undefined <-> id). Only a real vendor
        // switch should clear the typed code and require a new invoice code.
        if (
            previousVendorId == null ||
            vendorId == null ||
            previousVendorId === vendorId
        ) {
            return
        }

        setInvoiceCode('')
        setUnlockedVendorId(null)
        writeStoredVendorInvoice({
            vendorId,
            step: INVOICE_CODE_STEP,
        })
    }, [vendorId])

    const step =
        vendorId != null && unlockedVendorId === vendorId
            ? SEND_INVOICE_STEP
            : INVOICE_CODE_STEP

    const setStepWithStorage = (value: SetStateAction<number>) => {
        const next = typeof value === 'function' ? value(step) : value
        const currentVendorId = vendorIdRef.current
        if (next === SEND_INVOICE_STEP && currentVendorId != null) {
            setUnlockedVendorId(currentVendorId)
            writeStoredVendorInvoice({
                vendorId: currentVendorId,
                step: SEND_INVOICE_STEP,
            })
            return
        }
        setUnlockedVendorId(null)
        writeStoredVendorInvoice(
            currentVendorId == null
                ? null
                : { vendorId: currentVendorId, step: INVOICE_CODE_STEP },
        )
    }

    return {
        invoiceCode,
        step,
        setInvoiceCode,
        setStep: setStepWithStorage,
    }
}

export const useVendorInvoice = () => {
    const ctx = useContext(VendorInvoiceContext)

    const validateCode = async () => {
        try {
            const res = await axiosInstance.post(`invoice/validate_code/`, {
                invoice_code: ctx.invoiceCode,
            })
            const codeValid: boolean = res.data
            if (codeValid) {
                ctx.setStep(SEND_INVOICE_STEP)
            }
            return codeValid
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const sendInvoice = async (request: SendInvoiceRequest) => {
        try {
            const res = await axiosInstance.post('invoice/', request)
            const badWorkorderCount: number = res.data.bad_wo_list.length
            if (badWorkorderCount > 0) {
                toast.error(
                    `${res.data.bad_wo_list.length} Work Orders did not have pricing configured`,
                )
            }
            return badWorkorderCount
        } catch (e) {
            return Promise.reject(e)
        }
    }

    return { ...ctx, validateCode, sendInvoice }
}

export interface SendInvoiceRequest {
    apartment: number
    workorders: number[]
    invoice_code: string
}
